import * as Sentry from '@sentry/vue';

/**
 * Composable for handling chunk loading errors
 * 
 * Chunk loading errors occur when:
 * - User has old cached version of the app
 * - New deployment has different chunk hashes
 * - Network issues prevent chunk loading
 * 
 * Solution: Detect error and force reload to get fresh chunks
 */
export function useChunkErrorHandler() {
  const isChunkError = (error) => {
    const chunkFailedMessage = /Loading chunk [\d]+ failed|Failed to fetch dynamically imported module/i;
    return chunkFailedMessage.test(error?.message || '');
  };

  const handleChunkError = (error, context = {}) => {
    if (!isChunkError(error)) {
      return false;
    }

    // Log to Sentry with context
    Sentry.captureException(error, {
      tags: {
        errorType: 'chunk-load-error',
        ...context.tags
      },
      extra: {
        ...context.extra,
        errorMessage: error.message,
        timestamp: new Date().toISOString()
      }
    });

    // Check if we've already tried reloading
    const hasReloaded = sessionStorage.getItem('chunk-reload-attempted');
    
    if (!hasReloaded) {
      console.warn('Chunk loading failed, reloading page...', error.message);
      sessionStorage.setItem('chunk-reload-attempted', 'true');
      
      // Force reload with cache busting
      const url = context.targetUrl || window.location.href;
      window.location.href = url;
      
      return true;
    } else {
      // Already tried reloading, clear flag
      sessionStorage.removeItem('chunk-reload-attempted');
      console.error('Chunk loading failed after reload:', error);
      
      // Show user-friendly error message
      alert('Error al cargar la aplicación. Por favor, limpia la caché de tu navegador e intenta de nuevo.');
      
      return false;
    }
  };

  const clearReloadFlag = () => {
    sessionStorage.removeItem('chunk-reload-attempted');
  };

  return {
    isChunkError,
    handleChunkError,
    clearReloadFlag
  };
}
